import React, { useState } from 'react'
import { api } from "../util/APIConfig"

// Mock data for testing
/*const MOCK_TUTORS = [
  { tutorId: "1212", tutorName: "Tutor A" },
  { tutorId: "1213", tutorName: "Tutor B" }
]*/

function CSATFeedbackModal({ isOpen, onClose, tutors, feedbackType, toGiveFeedbackForSibling }) {

  //tutors = MOCK_TUTORS

  const [tutorRatings, setTutorRatings] = useState({})
  const [programRating, setProgramRating] = useState(0)
  const [feedback, setFeedback] = useState('')
  const [hasInterestedSibling, setHasInterestedSibling] = useState(false)
  const [siblingSchoolYear, setSiblingSchoolYear] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true)
      const tutorRatingsMap = Object.entries(tutorRatings)
        .filter(([_, rating]) => rating !== 'N/A')
        .map(([tutorId, rating]) => ({
          TutorId: tutorId,
          TutorName: tutors.find(tutor => tutor.tutorId === tutorId).tutorName,
          Rating: rating
        }));
      
      await api.post('/User/Student/SubmitCSATFeedback', {
        tutorRatings: tutorRatingsMap,
        programRating,
        feedback,
        feedbackType,
        hasInterestedSibling,
        siblingSchoolYear
      })
      
      onClose()
    } catch (error) {
      console.error("Error submitting feedback:", error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const RatingScale = ({ value, onChange }) => (
    <div>
      <div className="flex justify-between mb-4">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
          <button
            key={num}
            className={`px-5 py-1 rounded ${value === num ? 'bg-blue-500 text-white' : 'bg-gray-200 hover:bg-gray-300'}`}
            onClick={() => onChange(num)}
          >
            {num}
          </button>
        ))}
      </div>
      <div className="flex justify-between text-sm mb-6">
        <span>Very Dissatisfied</span>
        <span>Very Satisfied</span>
      </div>
    </div>
  )

  const RatingScaleWithNA = ({ value, onChange }) => (
    <div>
      <div className="flex justify-between gap-3 mb-4">
        <button
          className={`px-4 py-1 rounded relative group ${value === 'N/A' ? 'bg-blue-500 text-white' : 'bg-gray-200 hover:bg-gray-300'}`}
          onClick={() => onChange('N/A')}
        >
          N/A
          <div className="absolute bottom-full left-0 mb-2 px-3 py-2 bg-gray-800 text-white text-sm rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 w-[280px] text-center origin-top-left">
            Only select this option if you haven't completed a Tuition Session yet or this Tutor isn't your main Tutor.
          </div>
        </button>
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
          <button
            key={num}
            className={`px-4 py-1 rounded ${value === num ? 'bg-blue-500 text-white' : 'bg-gray-200 hover:bg-gray-300'}`}
            onClick={() => onChange(num)}
          >
            {num}
          </button>
        ))}
      </div>
      <div className="flex justify-between text-sm mb-6">
        <span>Very Dissatisfied</span>
        <span>Very Satisfied</span>
      </div>
    </div>
  )

  if (!isOpen) return null

  const allRatingsProvided = tutors.every(tutor => tutorRatings[tutor.tutorId] !== undefined) && programRating > 0
  const canSubmit = allRatingsProvided && feedback.trim().length > 0

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
      <div className="bg-white p-8 rounded-lg max-w-2xl w-full shadow-lg max-h-[90vh] overflow-y-auto relative">
        <h2 className="text-2xl font-bold mb-4 text-center">
          Programme Feedback
        </h2>
        
        {tutors.map(tutor => (
          <div key={tutor.tutorId} className="mb-6">
            <h3 className="text-xl font-bold mb-4">
              How satisfied are you with your 1-1 Tutoring with {tutor.tutorName}?
            </h3>
            <RatingScaleWithNA 
              value={tutorRatings[tutor.tutorId]} 
              onChange={(value) => setTutorRatings(prev => ({ ...prev, [tutor.tutorId]: value }))}
            />
          </div>
        ))}

        <h3 className="text-xl font-bold mb-4">
          How satisfied are you with your programme?
        </h3>
        <RatingScale 
          value={programRating}
          onChange={setProgramRating}
        />

        <div className="mb-6">
          <h3 className="text-xl font-bold mb-4">
            If you could improve one thing that would lead to a better experience, what would that be?
            <span className="text-red-500 ml-1">*</span>
          </h3>
          <textarea
            className={`w-full h-24 border rounded p-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              !feedback.trim() && 'border-red-500'
            }`}
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            placeholder="Please provide your feedback..."
            required
          />
        </div>

        {toGiveFeedbackForSibling && <div className="mb-6">
          <h3 className="text-xl font-bold mb-4">
            Do you have a sibling?
          </h3>
          <div className="flex gap-4">
            <button
              className={`px-6 py-2 rounded transition duration-200 ${
                hasInterestedSibling ? 'bg-blue-500 text-white' : 'bg-gray-200 hover:bg-gray-300'
              }`}
              onClick={() => setHasInterestedSibling(true)}
            >
              Yes
            </button>
            <button
              className={`px-6 py-2 rounded transition duration-200 ${
                !hasInterestedSibling ? 'bg-blue-500 text-white' : 'bg-gray-200 hover:bg-gray-300'
              }`}
              onClick={() => setHasInterestedSibling(false)}
            >
              No
            </button>
          </div>
        </div>}

        {toGiveFeedbackForSibling && hasInterestedSibling && <div className="mb-6">
          <h3 className="text-xl font-bold mb-4">
          What school year is your sibling in?
          </h3>
          <input
            className={`w-full h-10 border rounded p-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              !siblingSchoolYear && 'border-red-500'
            }`}
            value={siblingSchoolYear}
            onChange={(e) => setSiblingSchoolYear(e.target.value)}
          />
        </div>}

        <button
          className={`w-full px-4 py-2 rounded transition duration-200 ${
            canSubmit
              ? 'bg-blue-500 text-white hover:bg-blue-600'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
          onClick={handleSubmit}
          disabled={!canSubmit || isSubmitting}
        >
          {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
        </button>
      </div>
    </div>
  )
}

export default CSATFeedbackModal
